import type { IPublicClientApplication } from '@azure/msal-browser';
import type { IPlanFactoryAuth } from '@redirecthealth/plan-factory-ui';

/**
 * The host side of the package contract, over its own PublicClientApplication.
 * getAccessToken is silent only (like rnav libs/auth): when Entra needs
 * interaction it returns null, the package's 401 path calls onUnauthorized, and
 * that is the one place a login redirect starts.
 */
export function createMsalAuthAdapter(
  pca: IPublicClientApplication,
  loginScopes: string[],
): IPlanFactoryAuth {
  let redirecting = false;

  return {
    async getAccessToken(scopes, opts) {
      const account = pca.getActiveAccount() ?? pca.getAllAccounts()[0];
      if (!account) return null;
      try {
        const result = await pca.acquireTokenSilent({
          scopes,
          account,
          forceRefresh: opts?.forceRefresh ?? false,
        });
        return result.accessToken;
      } catch {
        // InteractionRequiredAuthError included: never start an interactive flow here.
        return null;
      }
    },

    onUnauthorized() {
      // Concurrent 401s must not start several redirects.
      if (redirecting) return;
      redirecting = true;
      pca.loginRedirect({ scopes: loginScopes }).catch(() => {
        redirecting = false;
      });
    },

    logout() {
      // postLogoutRedirectUri is already set on the PCA config (msalInstance.ts).
      return pca.logoutRedirect();
    },
  };
}
